import { clsx } from 'clsx';
import {
  type CSSProperties,
  type PropsWithChildren,
  type JSX,
  type ReactNode,
} from 'react';
import { ErrorBoundary } from 'react-error-boundary';

import { container } from './ChartState.sty.js';
import { EmptyState } from './EmptyState.js';
import { ErrorState } from './ErrorState.js';
import { useBehavioralTrackingProps } from '../../../../core/hooks/useBehavioralTrackingProps.js';
import type { BehaviorTrackingProps } from '../../../../core/types/behavior-tracking-props.js';
import type { MaskingProps } from '../../../../core/types/masking-props.js';
import { LoaderOverlay } from '../loader-overlay/LoaderOverlay.js';

interface ChartStateHandlerProps extends MaskingProps, BehaviorTrackingProps {
  empty: boolean;
  height: string | number;
  emptyState: ReactNode | undefined;
  errorState: ReactNode | ((message: string) => JSX.Element) | undefined;
  isLoading: boolean;
  style?: CSSProperties;
  className?: string;
}

const getContent = (props: PropsWithChildren<ChartStateHandlerProps>) => {
  const { empty, height, emptyState, errorState, children, isLoading } = props;

  if (empty) {
    return isLoading ? (
      <LoaderOverlay />
    ) : (
      <EmptyState height={height}>{emptyState}</EmptyState>
    );
  }

  return (
    <ErrorBoundary
      FallbackComponent={ErrorState({
        height: height,
        children: errorState,
      })}
    >
      {children}
      {isLoading && <LoaderOverlay />}
    </ErrorBoundary>
  );
};

export function ChartStateHandler(
  props: PropsWithChildren<ChartStateHandlerProps>,
) {
  const {
    height,
    empty,
    emptyState,
    errorState,
    isLoading,
    style,
    className,
    'data-dtrum-mask': dataDtrumMask,
    'data-dtrum-allow': dataDtrumAllow,
    ...rest
  } = props;
  const content = getContent(props);
  const [behavioralTrackingProps, otherProps] =
    useBehavioralTrackingProps(rest);

  return (
    <div
      {...behavioralTrackingProps}
      className={clsx(container, className)}
      style={{
        height,
        filter: isLoading ? 'opacity(85%)' : 'none',
        ...style,
      }}
      data-dtrum-mask={dataDtrumMask}
      data-dtrum-allow={dataDtrumAllow}
      {...otherProps}
    >
      {content}
    </div>
  );
}
